import React, { useState, useEffect } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import Input from '../../components/input/Input';
import { updateMonetization, getGuaranteeTypes } from '../../services/MonetizationService';
import { normalizeMonetizationLabel } from '../../utils/monetizationExecution';
import { formatCurrency } from '../../utils/Masks';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { formatOriginalDueDateDisplay } from '../../utils/earlyCancellationUtils';
import {
  formatCheckAssetQuantity,
  formatCheckAssetQuotation,
  formatCheckChpdLabel,
  formatCheckClearingLabel,
} from '../../utils/checkDetailDisplay';
import EarlyCancellationActions from './EarlyCancellationActions';
import ReinsuranceFrame from './ReinsuranceFrame';

interface GeneralTabProps {
  data: any;
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';
  onDataUpdate?: () => void;
  installmentRefreshKey?: number;
}

const MONETIZATION_TYPE_MAP: Record<string, number> = {
  check: 1,
  loan: 2,
  promissory_note: 3,
  commercial_endorsement: 4,
};

const resolveOfficerDisplayName = (detail: Record<string, unknown>): string => {
  const accountOfficer = typeof detail.account_officer === 'string' ? detail.account_officer.trim() : '';
  if (accountOfficer) return accountOfficer;

  const officerName = typeof detail.officer_name === 'string' ? detail.officer_name.trim() : '';
  if (officerName) return officerName;

  const officer = detail.officer;
  if (typeof officer === 'string' && officer.trim()) return officer.trim();
  if (officer && typeof officer === 'object' && 'name' in officer) {
    const name = (officer as { name?: string }).name;
    if (name?.trim()) return name.trim();
  }

  return '-';
};

const resolveBranchDisplayName = (detail: Record<string, unknown>): string => {
  const branch = typeof detail.bank_branch === 'string' ? detail.bank_branch.trim() : '';
  return branch || '-';
};

const GeneralTab: React.FC<GeneralTabProps> = ({ data, guaranteeType, onDataUpdate, installmentRefreshKey = 0 }) => {
  const fiscalId = useFiscalIdConfig();
  const [updating, setUpdating] = useState(false);
  const [guaranteeTypeOptions, setGuaranteeTypeOptions] = useState<{ id: number; label: string }[]>([]);

  // Helper function to convert string/boolean to boolean or null
  const parseBooleanValue = (value: any): boolean | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') return true;
      if (value.toLowerCase() === 'false') return false;
    }
    return null;
  };

  useEffect(() => {
    const normalizedLabel = data?.product_line_label ? normalizeMonetizationLabel(data.product_line_label) : '';
    const isPublicOps = normalizedLabel === 'operaciones publicas';
    const isObligacionNegociable = normalizedLabel === 'obligacion negociable';

    const fetchGuaranteeTypes = async () => {
      try {
        let response;
        if (isPublicOps) {
          response = await getGuaranteeTypes(undefined, 'OPERACIONES PUBLICAS');
        } else if (isObligacionNegociable) {
          response = await getGuaranteeTypes(undefined, 'OBLIGACIONES NEGOCIABLES');
        } else {
          const monetizationTypeId = MONETIZATION_TYPE_MAP[guaranteeType];
          if (!monetizationTypeId) return;
          response = await getGuaranteeTypes(monetizationTypeId);
        }
        if (response.success && 'data' in response && Array.isArray(response.data)) {
          setGuaranteeTypeOptions(
            response.data.map((gt: any) => ({
              id: gt.id,
              label: gt.name_end_user || gt.name,
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching guarantee types:', error);
      }
    };
    fetchGuaranteeTypes();
  }, [guaranteeType, data?.product_line_label]);
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('es-AR');
  };

  const formatMonetizationLoadDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    return formatDate(dateString);
  };

  const getOperationType = (): string => {
    if (guaranteeType === 'loan') return 'préstamo';
    if (guaranteeType === 'check') return 'cheque';
    if (guaranteeType === 'promissory_note') return 'pagaré';
    if (guaranteeType === 'commercial_endorsement') return 'commercial-endorsements';
    return 'préstamo';
  };

  const handleFieldUpdate = async (fieldName: string, value: any) => {
    if (updating) return;
    
    setUpdating(true);
    try {
      const updateData = { 
        [fieldName]: value,
        application_product_line_id: data.application_product_line_id
      };
      const response = await updateMonetization(getOperationType(), data.id_global, updateData);
      
      if (response.success) {
        if (onDataUpdate) {
          onDataUpdate();
        }
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
    } finally {
      setUpdating(false);
    }
  };

  const handleGuaranteeTypeChange = (value: string) => {
    const selected = guaranteeTypeOptions.find(opt => opt.label === value);
    if (selected) {
      handleFieldUpdate('guarantee_type_id', selected.id);
    }
  };

  const SECTION_HEADER_STYLE: React.CSSProperties = {
    backgroundColor: '#E6F5F0',
    padding: '12px 16px',
    borderRadius: '8px',
    margin: '0 0 16px 0',
  };

  const GENERAL_DATA_GRID_ROW: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '16px',
    alignItems: 'center',
  };

  const renderSectionHeader = (title: string) => (
    <div style={SECTION_HEADER_STYLE}>
      <H variant="secondary-2" type="4" weight="bold" modifier="strong" withMargin={false}>
        {title}
      </H>
    </div>
  );

  const InfoField = ({
    label,
    value,
    isCurrency = false,
    singleLine = false,
    allowEmpty = false,
  }: {
    label: string;
    value: string | number;
    isCurrency?: boolean;
    singleLine?: boolean;
    allowEmpty?: boolean;
  }) => {
    const displayValue = isCurrency
      ? (value != null && value !== '' ? formatCurrency(value) : '-')
      : (allowEmpty ? (value ?? '') : (value || '-'));

    return (
      <div style={{ display: 'flex', gap: '4px', alignItems: 'baseline', minWidth: 0 }}>
        <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ flexShrink: 0 }}>
          {label}:
        </P>
        <P
          type='3'
          variant='neutral'
          withMargin={false}
          style={
            singleLine
              ? {
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }
              : undefined
          }
        >
          {displayValue}
        </P>
      </div>
    );
  };

  const getOperationTypeDisplay = (): string => {
    if (data.operation_type) return data.operation_type;
    if (guaranteeTypeOptions.length > 0 && data.guarantee_type_id) {
      return guaranteeTypeOptions.find((opt) => opt.id === data.guarantee_type_id)?.label || '';
    }
    return '';
  };

  const formatIssueDueDateRange = (): string => {
    const issueDate = formatDate(data.issue_date);
    const dueDate = formatDate(data.due_date);
    if (issueDate === '-' && dueDate === '-') return '-';
    if (issueDate === '-') return dueDate;
    if (dueDate === '-') return issueDate;
    return `${issueDate} al ${dueDate}`;
  };

  const renderLoanGeneralData = () => (
    <>
      {renderSectionHeader('Datos Generales')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Numero de Aval" value={data.id_global} />
        <InfoField label="Razon Social" value={data.company_name} singleLine />
        <InfoField label={fiscalId.label} value={fiscalId.format(data.cuit)} />
        <InfoField label="Tipo de Operacion" value={getOperationTypeDisplay()} />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px' }}>
        <InfoField label="Tipo de Garantia" value={data.guarantee_type} />
        <InfoField label="Fecha de Carga" value={formatDate(data.created_at)} />
        <InfoField label="Divisa de la linea" value={data.currency} />
        <span aria-hidden="true" />
      </div>
    </>
  );

  const renderCheckGeneralData = () => (
    <>
      {renderSectionHeader('Datos Generales')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Numero de Aval" value={data.id_global} />
        <InfoField label="Razon Social" value={data.company_name} singleLine />
        <InfoField label={fiscalId.label} value={fiscalId.format(data.cuit)} />
        <InfoField label="Tipo de Operacion" value={getOperationTypeDisplay()} />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px' }}>
        <InfoField label="Tipo de Garantia" value={data.guarantee_type} />
        <InfoField
          label="Fecha de Carga"
          value={formatMonetizationLoadDate(data.monetization_date)}
          allowEmpty
        />
        <InfoField label="Divisa" value={data.currency} />
        <span aria-hidden="true" />
      </div>
    </>
  );

  const renderGeneralData = () => {
    if (guaranteeType === 'check') {
      return renderCheckGeneralData();
    }

    if (guaranteeType === 'loan') {
      return renderLoanGeneralData();
    }

    return (
      <>
        {renderSectionHeader('Datos Generales')}
        <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px' }}>
          <InfoField label="Numero de Aval" value={data.id_global} />
          <InfoField label="Razon Social" value={data.company_name} />
          <InfoField label={fiscalId.label} value={fiscalId.format(data.cuit)} />
          <Card>
            <DropDown
              id="guarantee-type-detail"
              label="Tipo de Operacion"
              title={
                guaranteeTypeOptions.length > 0
                  ? (guaranteeTypeOptions.find(opt => opt.id === data.guarantee_type_id)?.label || data.operation_type || '')
                  : (data.operation_type || '')
              }
              items={guaranteeTypeOptions.map(opt => ({ label: opt.label }))}
              onChange={handleGuaranteeTypeChange}
              size="md"
              disabled={updating || guaranteeTypeOptions.length === 0}
            />
            {guaranteeTypeOptions.length === 0 && data.operation_type && (
              <P type="4" variant="neutral" withMargin={false}>
                {data.operation_type}
              </P>
            )}
          </Card>
          <InfoField label="Tipo de Garantia" value={data.guarantee_type} />
          <InfoField label="Fecha de Carga" value={formatDate(data.created_at)} />
        </div>
      </>
    );
  };

  const renderLoanCreditData = () => (
    <>
      {renderSectionHeader('Datos del credito')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Monto bruto" value={data.monetization_amount} isCurrency />
        <InfoField label="Tipo de activo" value={data.asset_name || '-'} />
        <InfoField label="Monto en activo" value={formatCheckAssetQuantity(data.asset_quantity)} />
        <InfoField label="Cotizacion de activo" value={formatCheckAssetQuotation(data.asset_quotation)} />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Monto neto" value={data.net_monetization_amount} isCurrency />
        <InfoField label="Fecha de emision" value={formatDate(data.issue_date)} />
        <InfoField label="Fecha de vencimiento" value={formatDate(data.due_date)} />
        <InfoField label="Banco acreedor" value={data.creditor} singleLine />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Sistema de amortizacion" value={data.amortization_type} />
        <InfoField label="Periodicidad de pagos" value={data.payment_frequency_type} />
        <InfoField label="Destino" value={data.purpose_type || data.purpose_type_name || '-'} />
        <InfoField label="Plazo en dias" value={data.term_in_days} />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Estado de garantia" value={data.status_type} />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </div>
      <EarlyCancellationActions
        data={data}
        guaranteeType="loan"
        onSuccess={onDataUpdate}
        installmentRefreshKey={installmentRefreshKey}
      />
    </>
  );

  const renderReinsuranceFrame = (
    frameGuaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement',
    marginBottom?: string,
  ) => (
    <ReinsuranceFrame
      guaranteeType={frameGuaranteeType}
      idGlobal={String(data.id_global)}
      initialReinsured={parseBooleanValue(data.reinsured)}
      initialInstitutionId={data.reinsurance_institution_id ?? null}
      initialInstitutionName={data.reinsurance_institution_name ?? null}
      initialPercentage={data.reinsurance_percentage ?? null}
      initialReinsuredEntityPays={parseBooleanValue(data.reinsured_entity_pays)}
      marginBottom={marginBottom}
    />
  );

  const renderLoanReinsuranceData = () => (
    <>
      {renderSectionHeader('Datos de reafianzamiento')}
      {renderReinsuranceFrame('loan')}
    </>
  );

  const renderAdditionalDataFields = () => (
    <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px' }}>
      <InfoField label="Referido / Vendedor" value={data.referrer_name} />
      <InfoField label="Oficial (Nombre)" value={resolveOfficerDisplayName(data)} />
      <InfoField label="Sucursal" value={resolveBranchDisplayName(data)} />
      <span aria-hidden="true" />
    </div>
  );

  const renderLoanAdditionalData = () => (
    <>
      {renderSectionHeader('Datos adicionales')}
      {renderAdditionalDataFields()}
    </>
  );

  const renderCheckData = () => (
    <>
      {renderSectionHeader('Datos del cheque')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Monto bruto" value={data.monetization_amount} isCurrency />
        <InfoField label="Tipo de activo" value={data.asset_name || '-'} />
        <InfoField label="Monto en activo" value={formatCheckAssetQuantity(data.asset_quantity)} />
        <InfoField label="Cotizacion de activo" value={formatCheckAssetQuotation(data.asset_quotation)} />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Monto neto" value={data.net_monetization_amount} isCurrency />
        <InfoField label="Tipo CHPD" value={formatCheckChpdLabel(data.chpd)} />
        <InfoField label="Formato de cheque" value={data.check_format_type} />
        <InfoField label="Fecha de emision" value={formatDate(data.issue_date)} />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Fecha de vencimiento" value={formatDate(data.due_date)} />
        <InfoField label="Plazo en dias" value={data.term_in_days} />
        <InfoField label="Acreedor" value={data.creditor} />
        <InfoField label="Entidad bancaria" value={data.bank_entity} singleLine />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Nro de Cheque" value={data.check_number} />
        <InfoField label="Nro de Echeq" value={data.echeck_number} />
        <InfoField label="Codigo postal" value={data.postal_code} />
        <span aria-hidden="true" />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="CMC7" value={data.cmc7} singleLine />
        <InfoField label="Plazo de clearing" value={formatCheckClearingLabel(data.clearing)} />
        <InfoField label="Fecha estimada de pago" value={formatDate(data.estimated_payment_date)} />
        <InfoField label="Fecha real de pago" value={formatDate(data.payment_date)} />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px' }}>
        <InfoField label="Estado de garantia" value={data.status_type} />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </div>
      <EarlyCancellationActions
        data={data}
        guaranteeType="check"
        onSuccess={onDataUpdate}
      />
    </>
  );

  const renderCheckReinsuranceData = () => (
    <>
      {renderSectionHeader('Datos de reafianzamiento')}
      {renderReinsuranceFrame('check')}
    </>
  );

  const renderCheckAdditionalData = () => (
    <>
      {renderSectionHeader('Datos adicionales')}
      {renderAdditionalDataFields()}
    </>
  );

  const renderPromissoryNoteData = () => (
    <>
      {renderSectionHeader('Datos del pagare')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InfoField label="Monto bruto" value={data.monetization_amount} isCurrency />
        <InfoField label="Monto neto" value={data.net_monetization_amount} isCurrency />
        <InfoField label="Divisa" value={data.currency} />
        <InfoField label="Fecha de emision" value={formatDate(data.issue_date)} />
        <InfoField label="Fecha de vencimiento" value={formatDate(data.due_date)} />
        {data.original_due_date && (
          <InfoField
            label="Fecha de vencimiento original"
            value={formatOriginalDueDateDisplay(data.original_due_date)}
          />
        )}
        <InfoField label="Banco acreedor" value={data.creditor} />
        <InfoField label="Entidad bancaria" value={data.bank_entity} />
        <InfoField label="Plazo en dias" value={data.term_in_days} />
        <InfoField label="Endosado" value={data.endorsed === 'true' || data.endorsed === true ? 'Sí' : 'No'} />
        <InfoField label="ID E-Pagare" value={data.epromissory_note_id} />
        <InfoField label="Fecha estimada de pago" value={formatDate(data.estimated_payment_date)} />
        <InfoField label="Fecha real de pago" value={formatDate(data.payment_date)} />
        <InfoField label="Estado de garantia" value={data.status_type} />
        <div style={{ gridColumn: 'span 4' }}>{renderReinsuranceFrame('promissory_note', '0')}</div>
      </div>
      <EarlyCancellationActions
        data={data}
        guaranteeType="promissory_note"
        onSuccess={onDataUpdate}
      />
    </>
  );

  const renderCommercialEndorsementData = () => (
    <>
      {renderSectionHeader('Datos del aval comercial')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px', alignItems: 'center' }}>
        <InfoField label="Monto bruto" value={data.monetization_amount} isCurrency />
        <InfoField label="Divisa" value={data.currency} />
        <InfoField label="Fecha de emision" value={formatDate(data.issue_date)} />
        <InfoField label="Fecha de vencimiento" value={formatDate(data.due_date)} />
        {data.original_due_date && (
          <InfoField
            label="Fecha de vencimiento original"
            value={formatOriginalDueDateDisplay(data.original_due_date)}
          />
        )}
        <InfoField label="Acreedor" value={data.creditor} />
        <InfoField label="Plazo en dias" value={data.term_in_days} />
        <InfoField label="Destino" value={data.purpose_type || '-'} />
        <InfoField label="Estado de garantia" value={data.status_type} />
        <div style={{ gridColumn: 'span 4' }}>{renderReinsuranceFrame('commercial_endorsement', '0')}</div>
      </div>
      <EarlyCancellationActions
        data={data}
        guaranteeType="commercial_endorsement"
        onSuccess={onDataUpdate}
      />
    </>
  );

  const renderAdditionalData = () => (
    <>
      {renderSectionHeader('Datos adicionales')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '16px', alignItems: 'center' }}>
        <InfoField label="Referido / Vendedor" value={data.referrer_name} />
        <InfoField label="Oficial (Nombre)" value={resolveOfficerDisplayName(data)} />
        <InfoField label="Sucursal" value={resolveBranchDisplayName(data)} />
        <span aria-hidden="true" />
      </div>
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px', alignItems: 'center' }}>
        <Card>
          <P withMargin={false} variant='neutral' type='4' modifier='strong'>
            Fecha de cancelada
          </P>
          <Card margin={{ top: '2' }}>
            <DatePicker
              defaultValue={data.cancelled_date || ''}
              onChange={(date: string) => handleFieldUpdate('cancelled_date', date)}
              disabled={updating}
              displayFormat="DD/MM/AA"
            />
          </Card>
        </Card>
        <Card>
          <P withMargin={false} variant='neutral' type='4' modifier='strong'>
            Fecha de anulada
          </P>
          <Card margin={{ top: '2' }}>
            <DatePicker
              defaultValue={data.annulled_date || ''}
              onChange={(date: string) => handleFieldUpdate('annulled_date', date)}
              disabled={updating}
              displayFormat="DD/MM/AA"
              alignLeft={true}
            />
          </Card>
        </Card>
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </div>
    </>
  );

  const renderLoanRateData = () => (
    <>
      {renderSectionHeader('Tasa y Gracia')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px' }}>
        <InfoField label="Tasa" value={data.rate_type} />
        <InfoField label="Puntos Porcentuales" value={data.percentage_point} />
        <InfoField label="Periodo de Gracia" value={data.grace_period} />
        <span aria-hidden="true" />
      </div>
    </>
  );

  const renderLoanBalances = () => (
    <>
      {renderSectionHeader('Saldos')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px' }}>
        <InfoField
          label="Saldo operativo"
          value={data.operational_balance || '-'}
          isCurrency={!!data.operational_balance && data.operational_balance !== '-'}
        />
        <InfoField
          label="Saldo operativo (bruto)"
          value={data.gross_operational_balance || '-'}
          isCurrency={!!data.gross_operational_balance && data.gross_operational_balance !== '-'}
        />
        <InfoField
          label="Saldo vigente"
          value={data.current_balance || '-'}
          isCurrency={!!data.current_balance && data.current_balance !== '-'}
        />
        <InfoField
          label="Saldo vigente ponderado"
          value={data.weighted_current_balance || '-'}
          isCurrency={!!data.weighted_current_balance && data.weighted_current_balance !== '-'}
        />
      </div>
    </>
  );

  const renderCheckDrawerData = () => (
    <>
      {renderSectionHeader('Datos del librador')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px' }}>
        <InfoField label="Librador" value={data.drawer_name} singleLine />
        <InfoField label={fiscalId.drawerLabel} value={fiscalId.format(data.drawer_cuit)} />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </div>
    </>
  );

  const renderCheckBalances = () => (
    <>
      {renderSectionHeader('Saldos')}
      <div style={{ ...GENERAL_DATA_GRID_ROW, marginBottom: '32px' }}>
        <InfoField
          label="Saldo operativo"
          value={data.operational_balance || '-'}
          isCurrency={!!data.operational_balance && data.operational_balance !== '-'}
        />
        <InfoField
          label="Saldo operativo (bruto)"
          value={data.gross_operational_balance || '-'}
          isCurrency={!!data.gross_operational_balance && data.gross_operational_balance !== '-'}
        />
        <InfoField
          label="Saldo vigente"
          value={data.current_balance || '-'}
          isCurrency={!!data.current_balance && data.current_balance !== '-'}
        />
        <InfoField
          label="Saldo vigente ponderado"
          value={data.weighted_current_balance || '-'}
          isCurrency={!!data.weighted_current_balance && data.weighted_current_balance !== '-'}
        />
      </div>
    </>
  );

  const renderPromissoryNoteDrawerData = () => (
    <>
      {renderSectionHeader('Datos del librador')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InfoField label="Librador" value={data.drawer_name} />
        <InfoField label="Tipo de segmentación" value="-" />
        <InfoField label={fiscalId.drawerLabel} value={fiscalId.format(data.drawer_cuit)} />
        <InfoField label="Nombre del Librador" value="-" />
      </div>
    </>
  );

  const renderBalances = () => (
    <>
      {renderSectionHeader('Saldos')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InfoField label="Saldo Operativo" value={data.operational_balance || '-'} isCurrency={!!data.operational_balance && data.operational_balance !== '-'} />
        <InfoField label="Saldo Operativo (Bruto)" value={data.gross_operational_balance || '-'} isCurrency={!!data.gross_operational_balance && data.gross_operational_balance !== '-'} />
        <InfoField label="Saldo Vigente" value={data.current_balance || '-'} isCurrency={!!data.current_balance && data.current_balance !== '-'} />
        <InfoField label="Saldo Vigente Ponderado" value={data.weighted_current_balance || '-'} isCurrency={!!data.weighted_current_balance && data.weighted_current_balance !== '-'} />
      </div>
    </>
  );

  return (
    <Card spacing="4">
      {renderGeneralData()}
      
      {guaranteeType === 'loan' && renderLoanCreditData()}
      {guaranteeType === 'check' && renderCheckData()}
      {guaranteeType === 'promissory_note' && renderPromissoryNoteData()}
      {guaranteeType === 'commercial_endorsement' && renderCommercialEndorsementData()}

      {guaranteeType === 'loan' && renderLoanReinsuranceData()}
      {guaranteeType === 'loan' && renderLoanAdditionalData()}
      {guaranteeType === 'loan' && renderLoanRateData()}
      {guaranteeType === 'loan' && renderLoanBalances()}

      {guaranteeType === 'check' && renderCheckReinsuranceData()}
      {guaranteeType === 'check' && renderCheckAdditionalData()}
      {guaranteeType === 'check' && renderCheckDrawerData()}
      {guaranteeType === 'check' && renderCheckBalances()}

      {guaranteeType !== 'check' && guaranteeType !== 'loan' && renderAdditionalData()}

      {guaranteeType === 'promissory_note' && renderPromissoryNoteDrawerData()}

      {guaranteeType !== 'check' && guaranteeType !== 'loan' && renderBalances()}
    </Card>
  );
};

export default GeneralTab;
